import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, DefaultRootState } from "react-redux";
import "../../../ChatStyle.less";
import { useChat } from "../../../../../../../context/ChatContext/ChatContext";
import ChatMessage from "./ChatMessage";
import IntlMessages from "../../../../../../../util/IntlMessages";
import { Message } from "../../../../../../BP2Components";
import { parseBotMessage } from "../../../ChatScripts";
import { sendEmailMessage } from "../../../../../../../services/restAPI/RestApi";

const PAGE_SIZE = 100;

interface ChatMessagesContainerProps {}

const ChatMessagesContainer: React.FC<ChatMessagesContainerProps> = () => {
   const chatSettings = useSelector((state: DefaultRootState) => state.chatSettings);
   const { oldId } = useSelector((state: DefaultRootState) => state?.user);
   const { mailBoxes } = useSelector(({ messages }) => messages);

   const chatData = useChat();

   const containerRef = useRef<HTMLDivElement>(null);
   const messagesEndRef = useRef<HTMLDivElement | null>(null);
   const prevScrollHeightRef = useRef<number | null>(null);
   const isAtBottomRef = useRef(true);
   const isLoadingMoreRef = useRef(false);

   const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
   const prevRoomIdRef = useRef(chatSettings?.currentRoom?.id);

   const roomChanged = prevRoomIdRef.current !== chatSettings?.currentRoom?.id;
   const effectiveDisplayCount = roomChanged ? PAGE_SIZE : displayCount;

   const allRoomMessages = useMemo(
      () =>
         chatData?.chatData?.messages?.filter(
            (message) => message?.roomId === chatSettings?.currentRoom?.id,
         ),
      [chatData?.chatData?.messages, chatSettings?.currentRoom?.id],
   );

   const totalCount = allRoomMessages?.length ?? 0;
   const hasMore = totalCount > effectiveDisplayCount;
   const displayedMessages = allRoomMessages?.slice(-effectiveDisplayCount) ?? [];

   // reset pagination and scroll to bottom when switching rooms
   useEffect(() => {
      prevRoomIdRef.current = chatSettings?.currentRoom?.id;
      setDisplayCount(PAGE_SIZE);
      isAtBottomRef.current = true;
      if (messagesEndRef.current) {
         messagesEndRef.current.scrollIntoView({ behavior: "instant" });
      }
   }, [chatSettings?.currentRoom?.id]);

   // scroll to bottom when new message arrives and user is at bottom
   useEffect(() => {
      if (isAtBottomRef.current && messagesEndRef.current) {
         messagesEndRef.current.scrollIntoView({ behavior: "instant" });
      }
   }, [totalCount]);

   // restore scroll position after loading more messages
   useEffect(() => {
      isLoadingMoreRef.current = false;
      if (prevScrollHeightRef.current !== null && containerRef.current) {
         containerRef.current.scrollTop =
            containerRef.current.scrollHeight - prevScrollHeightRef.current;
         prevScrollHeightRef.current = null;
      }
   }, [displayCount]);

   const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 50;

      if (el.scrollTop <= 1 && hasMore && !isLoadingMoreRef.current) {
         isLoadingMoreRef.current = true;
         prevScrollHeightRef.current = el.scrollHeight;
         setDisplayCount((prev) => prev + PAGE_SIZE);
      }
   };

   const handleSendEmail = async (e, message) => {
      e.preventDefault();

      if (mailBoxes?.length > 1) return Message.error("mail service error. Email cannot be sended");

      const bodyFormData = new FormData();

      bodyFormData.append("mailboxId", `${mailBoxes?.[0]?.id}`);
      bodyFormData.append("message", parseBotMessage(message?.content)?.suggestedEmailText);
      bodyFormData.append("subject", `${"re:" + " " + chatSettings?.currentRoom?.mailTitle}`);
      bodyFormData.append("to", parseBotMessage(message?.content)?.assistantUtils?.sender);

      try {
         await sendEmailMessage(bodyFormData, "send").then(() =>
            Message.success("Wiadomość została wysłana"),
         );
      } catch (err) {
         console.log(err);
         Message.error("Problem z wysyłką maila. Mail nie mógł zostać wysłany.");
      }
   };

   return (
      <div className="main-messages-container" ref={containerRef} onScroll={handleScroll}>
         {hasMore && (
            <div className="chat-messages-load-more-indicator">
               <IntlMessages id="chat.scrollUpForMore" />
            </div>
         )}
         {displayedMessages?.length !== 0 ? (
            displayedMessages?.map((message, index) => (
               <ChatMessage
                  key={message.messageId}
                  message={message}
                  index={index}
                  oldId={oldId}
                  currentRoomMessages={displayedMessages}
                  handleSendEmail={handleSendEmail}
               />
            ))
         ) : (
            <div style={{ marginTop: "1rem" }}>
               <IntlMessages id="chat.noMessages" />
            </div>
         )}
         <div ref={messagesEndRef} />
      </div>
   );
};

export default ChatMessagesContainer;
